import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";

import useStaffTasksV2 from "../hooks/use-staff-tasks-v2.hook";

/** `?task=<id>`: the link in a task @mention email (TEAM_MENTIONS_FEATURE). */
export const TASK_DEEP_LINK_PARAM = "task";

/**
 * Opens the task view dialog for `?task=<id>` once, then strips the param so a reload, a back
 * navigation or a tab change does not open it again. Mounted inside `StaffTasksV2Provider`, which
 * wraps every page under "/", so the link works wherever it lands (the root redirect to /home
 * keeps the query string). A deleted or other-practice task shows the dialog's "not found" state.
 */
export default function StaffTaskDeepLink() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { handleOpenTaskModal } = useStaffTasksV2();
  const rawTaskId = searchParams.get(TASK_DEEP_LINK_PARAM);
  const taskId = Number(rawTaskId) || null;
  const openedForRef = useRef<number | null>(null);

  useEffect(() => {
    if (rawTaskId === null) {
      openedForRef.current = null;
      return;
    }
    if (taskId && openedForRef.current !== taskId) {
      openedForRef.current = taskId;
      handleOpenTaskModal({ mode: "view", taskId });
    }
    // react-router 6.3 has no functional form of setSearchParams: build the next params here.
    const next = new URLSearchParams(searchParams);
    next.delete(TASK_DEEP_LINK_PARAM);
    setSearchParams(next, { replace: true });
  }, [handleOpenTaskModal, rawTaskId, searchParams, setSearchParams, taskId]);

  return null;
}
